import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { ChevronDown, ExternalLink } from 'lucide-react'
import { t, type TranslationKey } from '@/shared/lib/i18n'
import { CONFIDENTIAL_MASK } from '@/shared/lib/confidential-mask'
import { instrumentPath } from '@/shared/lib/routes'
import { richTextToPlainText } from '@/shared/lib/rich-text-html'
import { resolveActivityFeedAlertCategory } from '@/shared/lib/maintenanceLogsActivityFeedAlert'
import { QA_ALERT_CATEGORY_MAP } from '@/shared/constants/qaAlertConfig'
import { Switch } from '@/shared/ui/switch'
import { Label } from '@/shared/ui/label'
import { Button } from '@/shared/ui/button'
import { CommentCountIndicator } from '@/shared/ui/comment-count-indicator'
import { cn } from '@/shared/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { ReportEmptyState } from '../components/ReportEmptyState'
import LogDetailsDialog from '@/pages/Instruments/components/ReportsTab/LogDetailsDialog'
import InstrumentServiceReportListDialog from '@/pages/Instruments/components/ReportsTab/InstrumentServiceReportListDialog'
import type WebMaintenanceLogsActivityFeedItem from '@/shared/types/web/WebMaintenanceLogsActivityFeedItem'
import type WebInstrumentStatusLog from '@/shared/types/web/WebInstrumentStatusLog'
import type WebInstrumentDepartmentLog from '@/shared/types/web/WebInstrumentDepartmentLog'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

interface MaintenanceLogsActivityFeedProps {
  instrumentId: number
  activityFeed: WebMaintenanceLogsActivityFeedItem[]
  alertOnly: boolean
  onAlertOnlyChange: (alertOnly: boolean) => void
}

const COLLAPSED_ITEM_COUNT = 5

interface TaskLogModalState {
  taskLogId: number
}

interface ServiceReportModalState {
  date: string
}

interface SummaryDetailModalState {
  eventType: 'status_change' | 'department_move' | 'task_deactivation' | 'review_action'
  statusDetail: WebInstrumentStatusLog | null
  departmentDetail: WebInstrumentDepartmentLog | null
  deactivationDetail: WebMaintenanceLogsActivityFeedItem['deactivationDetail']
  reviewItem: WebMaintenanceLogsActivityFeedItem | null
  timeZoneName: string
}

// ── Alert-only toggle ────────────────────────────────────────────────────────

const AlertToggle = ({
  alertOnly,
  successCount,
  onChange,
}: {
  alertOnly: boolean
  successCount: number
  onChange: (checked: boolean) => void
}) => {
  // Label describes the CURRENT mode (legacy calendarView.template.html:284-287), same as the
  // instrument History toggle: on → "Showing Alert only (N Successful Events Hidden)";
  // off → "Showing Full Details".
  const label = alertOnly
    ? `${t('instruments.show.history.showAlertsOnly')} ${t('instruments.show.history.successfulEventsHidden', { count: successCount })}`
    : t('instruments.show.history.showFullDetails')

  return (
    <div className="flex items-center gap-2 text-sm">
      <Switch
        id="maintenance-logs-history-alert-toggle"
        checked={alertOnly}
        onCheckedChange={onChange}
      />
      <Label htmlFor="maintenance-logs-history-alert-toggle" className="cursor-pointer">
        {label}
      </Label>
    </div>
  )
}

// ── Left color bar for a table row ───────────────────────────────────────────
// Mirrors the legacy yellow/blue/red left indicator stripe per event type.

const RowColorBar = ({ item }: { item: WebMaintenanceLogsActivityFeedItem }) => {
  const isBackdated = item.backdatedDetail != null
  const category = resolveActivityFeedAlertCategory({
    eventType: item.eventType,
    isBackdated,
    summaryText: item.summaryText,
  })
  if (!category) return <div className="w-1 shrink-0 self-stretch rounded-l" />

  const barColorClass = QA_ALERT_CATEGORY_MAP[category].dotColorClass
  return (
    <div
      className={cn('w-1 shrink-0 self-stretch rounded-l fill-current', barColorClass)}
      style={{ background: 'currentColor' }}
    />
  )
}

// ── Single activity feed row (table format) ──────────────────────────────────

const FeedTableRow = ({
  item,
  onClick,
}: {
  item: WebMaintenanceLogsActivityFeedItem
  onClick: (item: WebMaintenanceLogsActivityFeedItem) => void
}) => {
  const { formatDateTime } = useSiteDateTime()
  const isBackdated = item.backdatedDetail != null
  const backdatedReasonText = richTextToPlainText(item.backdatedDetail?.reason ?? '')
  const reviewExplanationText =
    item.eventType === 'review_action' ? richTextToPlainText(item.reviewDetail?.comments ?? '') : ''

  // Task column: prefer structured taskName; fall back to summaryText for events
  // like status_change / department_move that have no dedicated task_name.
  const taskDisplayText = item.taskName ?? item.summaryText
  const deptUnitLabel = [item.departmentName, item.unitName].filter(Boolean).join(' / ') || '—'
  const completedAtLabel = item.completedAt
    ? formatDateTime(item.completedAt, item.timeZoneName)
    : '—'
  // "Logged at" is the moment the action was recorded. For NC review rows event_at is the
  // scheduled missed instant (REBUILD-475: feed sort + "when missed"), so use the reviewer's
  // recorded_at here instead; fall back to event_at for migrated rows that lack it.
  const loggedAtSource =
    item.eventType === 'review_action'
      ? (item.reviewDetail?.recordedAt ?? item.eventAt)
      : item.eventAt
  const eventAtLabel = formatDateTime(loggedAtSource, item.timeZoneName)

  return (
    <button
      type="button"
      className="flex w-full items-stretch gap-0 text-left transition-colors hover:bg-muted/50"
      onClick={() => onClick(item)}
    >
      <RowColorBar item={item} />
      <div className="grid flex-1 grid-cols-[minmax(0,1.4fr)_7rem_6rem_7rem_7rem_8rem] gap-x-3 px-3 py-2 text-xs">
        {/* Task column */}
        <div className="min-w-0 space-y-0.5">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="block truncate text-sm font-medium" title={taskDisplayText}>
              {taskDisplayText}
            </span>
            {/* REBUILD-622: same indicator as the Reports list (task-result and service-report rows). */}
            <CommentCountIndicator count={item.commentCount} />
          </div>

          {/* Service-report problem description */}
          {item.subText && <span className="block text-muted-foreground">{item.subText}</span>}

          {/* Date-modified reason (not a second status chip — E52-S2) */}
          {isBackdated && item.backdatedDetail && (
            <div className="text-muted-foreground space-y-0.5">
              <span className="block">
                {t('reports.maintenanceLogs.activityFeed.dateModifiedReason')}
              </span>
              {backdatedReasonText && <span className="block pl-3">{backdatedReasonText}</span>}
            </div>
          )}

          {/* NC-confirmation / override explanation — legacy label, text rather than a count */}
          {reviewExplanationText && (
            <span className="block text-muted-foreground">
              <span className="font-medium">
                {t('reports.maintenanceLogs.activityFeed.explanation')}
              </span>{' '}
              {reviewExplanationText}
            </span>
          )}

          {/* Recorded data-field results */}
          {item.recordedResults.length > 0 && (
            <div className="space-y-0.5 text-muted-foreground">
              {item.recordedResults.map((result, index) => (
                <span key={index} className="block">
                  {result.label ? `${result.label}: ` : ''}
                  {result.isConfidentialHidden ? (
                    <span title={t('common.confidentialData')}>{CONFIDENTIAL_MASK}</span>
                  ) : (
                    <>
                      {result.value}
                      {result.acceptable != null && (
                        <span
                          className={cn(
                            'ml-1',
                            result.acceptable ? 'text-green-700' : 'text-destructive',
                          )}
                        >
                          {result.acceptable
                            ? t('reports.maintenanceLogs.activityFeed.acceptable')
                            : t('reports.maintenanceLogs.activityFeed.unacceptable')}
                        </span>
                      )}
                    </>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Single status label (task-detail vocabulary) */}
        <span className="truncate self-start font-medium" title={item.statusLabel ?? undefined}>
          {item.statusLabel ?? '—'}
        </span>

        {/* Completing user column */}
        <span
          className="truncate text-muted-foreground self-start"
          title={item.actorName ?? undefined}
        >
          {item.actorName ?? '—'}
        </span>

        {/* Completed at — entered completion time, no Date modified chip */}
        <span
          className="whitespace-nowrap text-muted-foreground self-start"
          title={completedAtLabel}
        >
          {completedAtLabel}
        </span>

        {/* Department / Unit */}
        <span className="truncate text-muted-foreground self-start" title={deptUnitLabel}>
          {deptUnitLabel}
        </span>

        {/* Logged at / event time */}
        <span className="whitespace-nowrap text-muted-foreground self-start" title={eventAtLabel}>
          {eventAtLabel}
        </span>
      </div>
    </button>
  )
}

// ── Detail dialogs (status change, department move, task deactivation) ───────

const DetailRow = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <div>
    <dt className="font-medium text-muted-foreground">{label}</dt>
    <dd>{value ?? '—'}</dd>
  </div>
)

const StatusChangeDetail = ({
  detail,
  timeZoneName,
}: {
  detail: WebInstrumentStatusLog
  timeZoneName: string
}) => {
  const { formatDateTime } = useSiteDateTime()
  return (
    <dl className="space-y-3 text-sm">
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.statusChangeDialog.oldStatus')}
        value={detail.oldStatusLabel}
      />
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.statusChangeDialog.newStatus')}
        value={detail.newStatusLabel}
      />
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.statusChangeDialog.changedBy')}
        value={detail.changedByName}
      />
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.statusChangeDialog.changedAt')}
        value={formatDateTime(detail.changedAt, timeZoneName)}
      />
      {detail.comments && (
        <DetailRow
          label={t('reports.maintenanceLogs.activityFeed.statusChangeDialog.comments')}
          value={detail.comments}
        />
      )}
    </dl>
  )
}

const DepartmentMoveDetail = ({
  detail,
  timeZoneName,
}: {
  detail: WebInstrumentDepartmentLog
  timeZoneName: string
}) => {
  const { formatDateTime } = useSiteDateTime()
  return (
    <dl className="space-y-3 text-sm">
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.departmentMoveDialog.from')}
        value={[detail.oldDepartmentName, detail.oldUnitName].filter(Boolean).join(' / ') || '—'}
      />
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.departmentMoveDialog.to')}
        value={[detail.newDepartmentName, detail.newUnitName].filter(Boolean).join(' / ') || '—'}
      />
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.departmentMoveDialog.changedBy')}
        value={detail.changedByName}
      />
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.departmentMoveDialog.changedAt')}
        value={formatDateTime(detail.changedAt, timeZoneName)}
      />
      {detail.comments && (
        <DetailRow
          label={t('reports.maintenanceLogs.activityFeed.departmentMoveDialog.comments')}
          value={detail.comments}
        />
      )}
    </dl>
  )
}

interface DeactivationDetailData {
  oldFrequency: string
  newFrequency: string
  taskName: string
  actorName: string
  performedAt: string
}

const DeactivationDetail = ({
  detail,
  timeZoneName,
}: {
  detail: DeactivationDetailData
  timeZoneName: string
}) => {
  const { formatDateTime } = useSiteDateTime()
  return (
    <dl className="space-y-3 text-sm">
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.deactivationDialog.taskName')}
        value={detail.taskName}
      />
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.deactivationDialog.oldFrequency')}
        value={detail.oldFrequency}
      />
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.deactivationDialog.newFrequency')}
        value={detail.newFrequency}
      />
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.deactivationDialog.performedBy')}
        value={detail.actorName}
      />
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.deactivationDialog.performedAt')}
        value={formatDateTime(detail.performedAt, timeZoneName)}
      />
    </dl>
  )
}

const ReviewActionDetail = ({ item }: { item: WebMaintenanceLogsActivityFeedItem }) => {
  const { formatDateTime } = useSiteDateTime()
  // NC review comments are stored as rich-text HTML (RichTextEditor on NC / Compliance forms).
  const commentsPlainText = richTextToPlainText(item.reviewDetail?.comments ?? '')

  return (
    <dl className="space-y-3 text-sm">
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.reviewActionDialog.taskName')}
        value={item.taskName ?? item.summaryText}
      />
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.reviewActionDialog.status')}
        value={item.statusLabel}
      />
      {/* event_at for a review row is the scheduled missed instant (REBUILD-475). Surface it
          here so users see both when the task was missed and when the reviewer acted. */}
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.reviewActionDialog.missedAt')}
        value={formatDateTime(item.eventAt, item.timeZoneName)}
      />
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.reviewActionDialog.reviewedBy')}
        value={item.actorName}
      />
      <DetailRow
        label={t('reports.maintenanceLogs.activityFeed.reviewActionDialog.reviewedAt')}
        value={formatDateTime(item.reviewDetail?.recordedAt ?? item.eventAt, item.timeZoneName)}
      />
      {commentsPlainText ? (
        <DetailRow
          label={t('reports.maintenanceLogs.activityFeed.reviewActionDialog.comments')}
          value={commentsPlainText}
        />
      ) : null}
    </dl>
  )
}

const SUMMARY_DIALOG_TITLE_KEYS: Record<SummaryDetailModalState['eventType'], TranslationKey> = {
  status_change: 'reports.maintenanceLogs.activityFeed.statusChangeDialog.title',
  department_move: 'reports.maintenanceLogs.activityFeed.departmentMoveDialog.title',
  task_deactivation: 'reports.maintenanceLogs.activityFeed.deactivationDialog.title',
  review_action: 'reports.maintenanceLogs.activityFeed.reviewActionDialog.title',
}

// ── Main component ───────────────────────────────────────────────────────────

const MaintenanceLogsActivityFeed = ({
  instrumentId,
  activityFeed,
  alertOnly,
  onAlertOnlyChange,
}: MaintenanceLogsActivityFeedProps) => {
  const [taskLogModal, setTaskLogModal] = useState<TaskLogModalState | null>(null)
  const [serviceReportModal, setServiceReportModal] = useState<ServiceReportModalState | null>(null)
  const [summaryDetailModal, setSummaryDetailModal] = useState<SummaryDetailModalState | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  // Count "successful" (non-alert) items so the toggle can show "N Hidden".
  const successCount = activityFeed.filter(
    (item) =>
      resolveActivityFeedAlertCategory({
        eventType: item.eventType,
        isBackdated: item.backdatedDetail != null,
        summaryText: item.summaryText,
      }) === null,
  ).length

  const visibleFeed = alertOnly
    ? activityFeed.filter(
        (item) =>
          resolveActivityFeedAlertCategory({
            eventType: item.eventType,
            isBackdated: item.backdatedDetail != null,
            summaryText: item.summaryText,
          }) !== null,
      )
    : activityFeed

  const handleItemClick = (item: WebMaintenanceLogsActivityFeedItem) => {
    if (item.eventType === 'task_log') {
      setTaskLogModal({ taskLogId: item.referenceId })
    } else if (item.eventType === 'review_action') {
      // Prefer Log Details when a satellite task log exists (legacy openScheduledAndFailure).
      // Otherwise open a read-only NC Review summary so the row is never a silent no-op.
      const linkedTaskLogId = item.reviewDetail?.taskLogId
      if (linkedTaskLogId != null) {
        setTaskLogModal({ taskLogId: linkedTaskLogId })
      } else {
        setSummaryDetailModal({
          eventType: 'review_action',
          statusDetail: null,
          departmentDetail: null,
          deactivationDetail: null,
          reviewItem: item,
          timeZoneName: item.timeZoneName,
        })
      }
    } else if (item.eventType === 'service_report') {
      const dateOnly = item.eventAt.slice(0, 10)
      setServiceReportModal({ date: dateOnly })
    } else if (item.eventType === 'status_change' || item.eventType === 'department_move') {
      setSummaryDetailModal({
        eventType: item.eventType as 'status_change' | 'department_move',
        statusDetail: item.statusDetail,
        departmentDetail: item.departmentDetail,
        deactivationDetail: null,
        reviewItem: null,
        timeZoneName: item.timeZoneName,
      })
    } else if (item.eventType === 'task_deactivation' && item.deactivationDetail) {
      setSummaryDetailModal({
        eventType: 'task_deactivation',
        statusDetail: null,
        departmentDetail: null,
        deactivationDetail: item.deactivationDetail,
        reviewItem: null,
        timeZoneName: item.timeZoneName,
      })
    }
  }

  const summaryDialogTitle = summaryDetailModal
    ? t(SUMMARY_DIALOG_TITLE_KEYS[summaryDetailModal.eventType])
    : ''

  return (
    <div className="space-y-3" data-testid="maintenance-logs-history">
      {/* Section header — single chronological history (E52-S1) */}
      <div className="space-y-2">
        <h2 className="text-base font-semibold">
          {t('reports.maintenanceLogs.activityFeed.title')}
        </h2>
        <Button variant="outline" size="sm" asChild className="w-fit">
          <Link href={instrumentPath(instrumentId)}>
            <ExternalLink className="h-4 w-4" aria-hidden />
            {t('reports.maintenanceLogs.activityFeed.viewInstrument')}
          </Link>
        </Button>
      </div>

      {/* Alert-only toggle */}
      <AlertToggle alertOnly={alertOnly} successCount={successCount} onChange={onAlertOnlyChange} />

      {visibleFeed.length === 0 ? (
        <ReportEmptyState
          message={
            alertOnly && activityFeed.length > 0
              ? t('instruments.reports.listView.emptyAlerts')
              : t('reports.maintenanceLogs.activityFeed.empty')
          }
        />
      ) : (
        <div className="rounded border divide-y divide-border overflow-hidden">
          {/* Table header — w-1 spacer aligns with the per-row color bar */}
          <div className="flex bg-muted/30 text-xs font-semibold text-muted-foreground">
            <div className="w-1 shrink-0" />
            <div className="grid flex-1 grid-cols-[minmax(0,1.4fr)_7rem_6rem_7rem_7rem_8rem] gap-x-3 px-3 py-1.5">
              <span>{t('instruments.reports.listView.colTask')}</span>
              <span>{t('reports.maintenanceLogs.colStatus')}</span>
              <span>{t('instruments.reports.listView.colUser')}</span>
              <span>{t('reports.maintenanceLogs.colCompletedAt')}</span>
              <span>{t('reports.maintenanceLogs.colDeptUnit')}</span>
              <span>{t('reports.maintenanceLogs.colLoggedAt')}</span>
            </div>
          </div>

          {/* First N rows always visible */}
          {visibleFeed.slice(0, COLLAPSED_ITEM_COUNT).map((item) => (
            <FeedTableRow
              key={`${item.eventType}-${item.id}`}
              item={item}
              onClick={handleItemClick}
            />
          ))}

          {/* Remaining rows, animated expand */}
          {visibleFeed.length > COLLAPSED_ITEM_COUNT && (
            <div
              className="grid transition-[grid-template-rows] duration-300 ease-in-out"
              style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
            >
              <div className="overflow-hidden">
                {visibleFeed.slice(COLLAPSED_ITEM_COUNT).map((item) => (
                  <div key={`${item.eventType}-${item.id}`} className="border-t border-border">
                    <FeedTableRow item={item} onClick={handleItemClick} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Show more / less button */}
          {visibleFeed.length > COLLAPSED_ITEM_COUNT && (
            <button
              type="button"
              className="flex w-full items-center justify-center gap-1.5 border-t border-border px-4 py-2.5 text-xs text-muted-foreground transition-colors hover:bg-muted/50"
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              {isExpanded
                ? t('reports.maintenanceLogs.activityFeed.showLess')
                : t('reports.maintenanceLogs.activityFeed.showMore').replace(
                    '{count}',
                    String(visibleFeed.length - COLLAPSED_ITEM_COUNT),
                  )}
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
              />
            </button>
          )}
        </div>
      )}

      <LogDetailsDialog
        instrumentId={instrumentId}
        instrumentTaskId={null}
        instrumentLabel=""
        serialNumber={null}
        taskLogId={taskLogModal?.taskLogId ?? null}
        onClose={() => setTaskLogModal(null)}
        onAfterDelete={() => router.reload()}
      />

      <InstrumentServiceReportListDialog
        instrumentId={instrumentId}
        date={serviceReportModal?.date ?? null}
        onClose={() => setServiceReportModal(null)}
      />

      <Dialog
        open={summaryDetailModal !== null}
        onOpenChange={(open) => {
          if (!open) setSummaryDetailModal(null)
        }}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{summaryDialogTitle}</DialogTitle>
          </DialogHeader>
          {summaryDetailModal?.eventType === 'status_change' && summaryDetailModal.statusDetail && (
            <StatusChangeDetail
              detail={summaryDetailModal.statusDetail}
              timeZoneName={summaryDetailModal.timeZoneName}
            />
          )}
          {summaryDetailModal?.eventType === 'department_move' &&
            summaryDetailModal.departmentDetail && (
              <DepartmentMoveDetail
                detail={summaryDetailModal.departmentDetail}
                timeZoneName={summaryDetailModal.timeZoneName}
              />
            )}
          {summaryDetailModal?.eventType === 'task_deactivation' &&
            summaryDetailModal.deactivationDetail && (
              <DeactivationDetail
                detail={summaryDetailModal.deactivationDetail}
                timeZoneName={summaryDetailModal.timeZoneName}
              />
            )}
          {summaryDetailModal?.eventType === 'review_action' && summaryDetailModal.reviewItem && (
            <ReviewActionDetail item={summaryDetailModal.reviewItem} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default MaintenanceLogsActivityFeed
